<template>
  <div class="card mb-3">
    <div
      class="card-header d-flex justify-content-between align-items-center"
      role="button"
      @click="showBody = !showBody"
    >
      <div>
        <span class="badge bg-secondary ms-2">{{ portfolioType }}</span>
        portfolio named:
        <code class="text-dark bg-light px-1">{{ portfolioName }}</code> •
        <strong>Volume:</strong> {{ volumeMwh }} MWh
      </div>
      <div class="text-muted">
        Last updated: {{ (lastUpdated ?? "").slice(0, 10) }}
      </div>
    </div>
    <div v-if="showBody" class="card-body p-0">
      <div v-if="rows.length === 0" class="p-3 text-muted text-center">
        No items in this portfolio
      </div>
      <div v-else>
        <div class="d-flex align-items-center gap-2 px-2 py-1 border-bottom">
          <div class="ms-auto" />
          <small v-if="isForwardEscrow" class="text-body-tertiary">
            Forwards escrow moves only on its deal page: Return to owner takes
            certificates out, Pull to escrow puts them in.
          </small>
          <template v-else>
            <select
              class="form-select form-select-sm w-auto"
              v-model.number="selectedTargetId"
            >
              <option :value="undefined">Select target portfolio…</option>
              <option
                v-for="p in filteredTargetPortfolios"
                :key="p.id"
                :value="p.id as number"
              >
                {{ p.name }} ({{ p.portfolio_type }})
              </option>
            </select>
            <button
              class="btn btn-sm btn-outline-primary"
              :disabled="!canMove"
              @click.stop="onMove"
            >
              Move
            </button>
          </template>
          <button
            v-if="portfolioType === 'UNALLOCATED'"
            class="btn btn-sm btn-outline-success"
            :disabled="disabledActions"
            @click.stop="emit('action:allocate')"
          >
            Allocate
          </button>
        </div>
        <AdminPortfolioItemsTable :items="rows" v-model="model" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  FlattenedPortfolioItem,
  PortfolioWithInstructions,
} from "@/types";
import { computed, ref } from "vue";
import AdminPortfolioItemsTable from "./AdminPortfolioItemsTable.vue";

const props = defineProps<{
  portfolioId: number;
  portfolioName: string;
  portfolioType: string;
  lastUpdated: string | null;
  volumeMwh: number;
  rows: FlattenedPortfolioItem[];
  disabledActions?: boolean;
  targetPortfolios: PortfolioWithInstructions[];
}>();

const model = defineModel<number[]>({ required: true });

const emit = defineEmits<{
  "action:move": [toPortfolioId: number];
  "action:allocate": [];
}>();

const showBody = ref(true);
const selectedTargetId = ref<number | undefined>(undefined);

const hasSelection = computed(() => model.value.length > 0);

// Every FORWARD item is forwards escrow, which the mover refuses to move in either direction: it
// leaves only through the deal's return-to-owner and enters only through its pull to escrow.
const isForwardEscrow = computed(() => props.portfolioType === "FORWARD");

const filteredTargetPortfolios = computed(() =>
  props.targetPortfolios.filter(
    (p) => p.id !== props.portfolioId && p.portfolio_type !== "FORWARD"
  )
);

const canMove = computed(
  () => hasSelection.value && !props.disabledActions && !!selectedTargetId.value
);

function onMove() {
  if (!selectedTargetId.value) return;
  emit("action:move", selectedTargetId.value);
}
</script>

<style scoped lang="scss"></style>
